/**
 * OpenDienstenRow — Row showing unassigned SF routes + open shifts at the top of the schedule grid
 */

import type { RouteListItem } from '../../lib/salesforce/types';
import type { ScheduleEntry } from '../../stores/schedule-store';
import { shiftChipClass } from './shift-style';

const shiftLabels: Record<string, string> = {
  wasserij: 'Wass.',
  route: 'Route',
  kantoor: 'Kantoor',
};

interface OpenDienstenRowProps {
  routes: RouteListItem[];
  openShifts: ScheduleEntry[];
  weekDates: string[];
  onRouteClick: (route: RouteListItem) => void;
  onShiftClick: (shift: ScheduleEntry) => void;
  onCellClick: (date: string) => void;
}

export function OpenDienstenRow({ routes, openShifts, weekDates, onRouteClick, onShiftClick, onCellClick }: OpenDienstenRowProps) {
  const openRoutes = routes.filter(r => !r.chauffeurId);
  const totalOpen = openRoutes.length + openShifts.length;

  return (
    <tr className="border-b border-amber-200 bg-amber-50/60">
      {/* Label column */}
      <td className="sticky left-0 z-10 bg-amber-50 p-3 border-r border-amber-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 bg-amber-200 text-amber-800">
            !
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-800">OPEN</p>
            <p className="text-[10px] text-amber-600">{totalOpen > 0 ? `${totalOpen} dienst${totalOpen !== 1 ? 'en' : ''}` : 'klik + om toe te voegen'}</p>
          </div>
        </div>
      </td>

      {/* Day cells */}
      {weekDates.map(date => {
        const dayRoutes = openRoutes.filter(r => r.datum === date);
        const dayShifts = openShifts
          .filter(s => s.date === date)
          .sort((a, b) => (a.time_from || '').localeCompare(b.time_from || ''));
        const hasItems = dayRoutes.length > 0 || dayShifts.length > 0;

        return (
          <td
            key={date}
            onClick={() => onCellClick(date)}
            className="p-1.5 border-r border-amber-100 align-top cursor-pointer hover:bg-amber-100/50"
          >
            {/* Open SF routes */}
            {dayRoutes.map(route => (
              <button
                key={route.id}
                onClick={(e) => { e.stopPropagation(); onRouteClick(route); }}
                className="w-full rounded-lg border border-amber-300 bg-amber-100 p-1.5 text-left transition-all hover:bg-amber-200 hover:shadow-sm mb-1 last:mb-0"
              >
                <div className="text-xs font-semibold text-amber-800 truncate">
                  {route.routeName || route.name}
                </div>
                <div className="text-[10px] text-amber-600">
                  {route.stopCountOphalen > 0 && <span>{route.stopCountOphalen}&uarr;</span>}
                  {route.stopCountOphalen > 0 && route.stopCountAfleveren > 0 && ' '}
                  {route.stopCountAfleveren > 0 && <span>{route.stopCountAfleveren}&darr;</span>}
                  {route.stopCountOphalen === 0 && route.stopCountAfleveren === 0 && 'geen stops'}
                </div>
              </button>
            ))}

            {/* Open shifts (no staff assigned) */}
            {dayShifts.map(shift => (
                <button
                  key={shift.id}
                  onClick={(e) => { e.stopPropagation(); onShiftClick(shift); }}
                  className={`w-full rounded-lg border ${shiftChipClass(shift.shift_type, shift.time_from)} p-1.5 text-left transition-all hover:shadow-sm mb-1 last:mb-0 border-dashed`}
                >
                  <div className="text-xs font-semibold truncate">
                    {shift.shift_type ? shiftLabels[shift.shift_type] : 'Dienst'}
                  </div>
                  <div className="text-[10px] opacity-75">
                    {shift.time_from?.slice(0, 5)}-{shift.time_to?.slice(0, 5)}
                  </div>
                </button>
            ))}

            {/* Add shift button — always visible */}
            <div
              className="text-[10px] text-amber-400 hover:text-amber-600 text-center py-1 cursor-pointer hover:bg-amber-100 rounded mt-0.5"
              onClick={(e) => { e.stopPropagation(); onCellClick(date); }}
            >
              + dienst
            </div>
          </td>
        );
      })}

      {/* Hours column */}
      <td className="p-3 text-center text-sm text-amber-400">-</td>
    </tr>
  );
}
